import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import { api, applyBranding, lastServerActivityMs, type Me, type PublicConfig } from "./api";
import { idlePhase, secondsLeft, syncedLastActivity, keepaliveAction } from "./idle";
import { syncClock, useTestClock, clearTestClock, type TestClockState } from "./clock";
import { SignIn, PinEntry } from "./SignIn";
import { Users } from "./Users";
import { Account } from "./Account";
import { Config } from "./Config";
import { Roster } from "./Roster";
import { ReviewQueue } from "./ReviewQueue";
import { RecordScreen } from "./RecordScreen";
import { Reports } from "./Reports";
import { Station } from "./Station";
import { Authorization } from "./Authorization";
import { Pending } from "./Pending";
import { Cities } from "./Cities";
import { Pass } from "./Pass";
import { Countersign } from "./Countersign";
import { Missed } from "./Missed";
import { PrintCenter } from "./PrintCenter";
import { Supplies } from "./Supplies";
import { Otc } from "./Otc";
import { Visits } from "./Visits";
import { Chart } from "./Chart";
import { Staff } from "./Staff";
import { mayAdministerRx, mayAdministerOtc, type Role } from "../server/roles";
import { TestOutbox } from "./TestOutbox";
import { TestReset } from "./TestReset";
import { SupportFooter } from "./support";
import "./index.css";

// Deliberately does NOT render shared/'s <App /> (the applicant form). This is
// its own component tree. Routing is a plain pathname switch: a handful of
// destinations, nothing nested.
//
// ONE build serves both programs: name, colors and logo arrive at runtime from
// /api/public-config.

/**
 * Placeholder sections for the rounds to come, shown only to roles that will
 * use them. cap null is for every signed-in user: My account, below the
 * role-specific items, and the testOnly items, which exist only in the test
 * environment (plan §8A), where alone the server registers their routes.
 */
const NAV: { path: string; label: string; cap: string | null; testOnly?: true; giverOnly?: true }[] = [
  { path: "/review", label: "Record review", cap: "record.read" },
  { path: "/roster", label: "Roster", cap: "record.read" },
  { path: "/station", label: "Med station", cap: "station.use" },
  { path: "/pending", label: "Pending authorizations", cap: "reports.screen" },
  { path: "/cities", label: "Cities", cap: "city.assign" },
  { path: "/pass", label: "Medication pass", cap: "station.use" },
  // Only for users who may be named as giving a dose (plan §9.1 #8); with a count badge.
  { path: "/countersign", label: "To countersign", cap: "record.read", giverOnly: true },
  { path: "/print", label: "Print center", cap: "export.phi" },
  { path: "/visits", label: "Visits", cap: "visit.create" },
  { path: "/supplies", label: "Supplies", cap: "supplies.manage" },
  // Staff medical records are read by the roles that read student records (Stage 2); the import itself is System Admin.
  { path: "/staff", label: "Staff", cap: "record.read" },
  { path: "/reports", label: "Reports", cap: "reports.screen" },
  { path: "/config", label: "Config", cap: "config.operational" },
  { path: "/users", label: "Users", cap: "users.manage" },
  { path: "/account", label: "My account", cap: null },
  { path: "/test-reset", label: "Reset test data", cap: null, testOnly: true },
  { path: "/test-outbox", label: "Test Outbox", cap: null, testOnly: true },
];

const POLL_MS = 60_000;
const WARN_BEFORE_MS = 5 * 60_000;
/**
 * Real input is forwarded to the server at most this often, with one trailing
 * send so the LAST input always arrives (idle.ts keepaliveAction).
 */
const KEEPALIVE_EVERY_MS = 30_000;

function Header({ cfg, me, onSignOut, testClock }: { cfg: PublicConfig | null; me: Me | null; onSignOut?: () => void; testClock?: TestClockState | null }) {
  return (
    <>
      {cfg?.environment === "test" && (
        <div className="bg-amber-300 px-4 py-1 text-center text-sm font-semibold text-amber-950 print:hidden" role="note">
          TEST ENVIRONMENT · made-up data only · emails other than sign-in go to the Test Outbox
          {testClock?.set && (
            <span className="block">
              Test clock: {testClock.label} ({testClock.window}) ·{" "}
              <button type="button" className="underline" onClick={() => void clearTestClock()}>Back to real time</button>
            </span>
          )}
        </div>
      )}
      <header className="bg-program text-white print:hidden">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          {cfg && <img src={cfg.logoPath} alt="" className="h-10 w-10 shrink-0 rounded-full bg-white object-contain p-0.5" />}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm opacity-90">{cfg?.programName ?? " "}</p>
            <h1 className="truncate text-base font-semibold sm:text-lg">Medical Administration</h1>
          </div>
          {me && (
            <div className="flex items-center gap-3 text-right">
              <div className="hidden sm:block">
                <p className="text-sm font-medium leading-tight">{me.name}</p>
                <p className="text-xs opacity-80">{me.roleLabel}{me.credential ? ` · ${me.credential}` : ""}</p>
              </div>
              <button type="button" onClick={onSignOut} className="rounded-md border border-white/40 px-3 py-1.5 text-sm hover:bg-white/10">
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>
    </>
  );
}

function Unavailable({ message }: { message: string }) {
  // A test-environment Reset in progress is a minute's wait, not an outage.
  const resetting = message.startsWith("Test data is being reset");
  return (
    <div className="section-card" role="alert">
      <h2 className="text-lg font-semibold mb-2">{resetting ? "One moment" : "Medical Administration is unavailable"}</h2>
      <p className="text-sm text-slate-700">{message}</p>
      {resetting ? (
        <button type="button" className="btn-secondary mt-3" onClick={() => window.location.reload()}>Try again</button>
      ) : (
        <p className="text-sm text-slate-700 mt-2">Use the paper packet until it is back.</p>
      )}
    </div>
  );
}

function IdleWarning({ secondsLeft, onStay, onSignOut }: { secondsLeft: number; onStay: () => void; onSignOut: () => void }) {
  const m = Math.floor(secondsLeft / 60);
  const s = String(secondsLeft % 60).padStart(2, "0");
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/50 p-4" role="alertdialog" aria-modal="true">
      <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-lg">
        <h2 className="text-lg font-semibold">Still there?</h2>
        <p className="mt-2 text-sm text-slate-700">
          For privacy you'll be signed out in <span className="font-mono font-semibold">{m}:{s}</span> unless you continue.
        </p>
        <div className="mt-4 flex gap-2">
          <button type="button" className="btn-primary" onClick={onStay} autoFocus>Stay signed in</button>
          <button type="button" className="btn-secondary" onClick={onSignOut}>Sign out</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Go-live readiness (plan §7.1 #1): on every screen, the settings still blank.
 * Information only: it never blocks the review queue.
 */
function ReadinessBanner({ version }: { version: number }) {
  const [r, setR] = useState<{ blank: { key: string; label: string }[]; canEdit: boolean } | null>(null);
  useEffect(() => {
    void api<{ readiness: { blank: { key: string; label: string }[] }; canEdit: boolean }>("GET", "/api/config/readiness").then((x) => {
      if (x.ok) setR({ blank: x.data.readiness.blank, canEdit: x.data.canEdit });
    });
  }, [version]);
  if (!r || r.blank.length === 0) return null;
  return (
    <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 print:hidden" role="note">
      <span className="font-semibold">Go-live readiness:</span> {r.blank.length} setting{r.blank.length === 1 ? " is" : "s are"} still blank
      ({r.blank.map((b) => b.label).join(", ")}). Record review works meanwhile.
      {r.canEdit && (
        <>
          {" "}
          <a href="/config" className="font-medium underline">Open Config</a>
        </>
      )}
    </div>
  );
}

function Home({
  me,
  path,
  environment,
  onMe,
}: {
  me: Me;
  path: string;
  environment: PublicConfig["environment"] | undefined;
  onMe: (user: Me) => void;
}) {
  const giver = mayAdministerRx({ role: me.role as Role, licensed: me.licensed, mayAdministerOtc: me.mayAdministerOtc }) || mayAdministerOtc({ role: me.role as Role, licensed: me.licensed, mayAdministerOtc: me.mayAdministerOtc });
  const allowed = NAV.filter(
    (n) => (!n.testOnly || environment === "test") && (!n.giverOnly || giver) && (n.cap === null || me.capabilities.includes(n.cap))
  );
  const [toSign, setToSign] = useState<number | null>(null);
  useEffect(() => {
    if (giver) void api<{ count: number }>("GET", "/api/countersign?count=1").then((r) => r.ok && setToSign(r.data.count));
  }, [giver, path]);
  // A person's record (/review/<PersonKey>) sits under "Record review".
  const recordKey = path.startsWith("/review/") ? decodeURIComponent(path.slice("/review/".length)) : null;
  // A report, its print view and the digest (/reports/...) sit under "Reports"; a visit under "Med station".
  const section = recordKey
    ? "/review"
    : path.startsWith("/reports/") || path === "/status-board"
      ? "/reports"
      : path.startsWith("/station/") || path.startsWith("/otc/") || path.startsWith("/chart/")
        ? "/station"
        : path.startsWith("/authorizations/")
          ? "/pending"
          : path.startsWith("/visits/")
            ? "/visits"
          : path.startsWith("/staff/")
            ? "/staff"
          : path.startsWith("/pass/") || path === "/missed"
            ? "/pass"
            : path.startsWith("/print/")
              ? "/print"
              : path;
  const here = allowed.find((n) => n.path === section);
  const [readinessVersion, setReadinessVersion] = useState(0);
  return (
    <div className="md:flex md:gap-6">
      {/* Desktop widths: the menu stays put while a long page scrolls (walk-through 2026-10-18); tablet and phone unchanged. */}
      <nav aria-label="Sections" className="mb-4 md:mb-0 md:w-48 md:shrink-0 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:self-start lg:overflow-y-auto print:hidden">
        <ul className="flex gap-2 overflow-x-auto pb-1 md:flex-col md:overflow-visible">
          {allowed.map((n) => (
            <li key={n.path} className="shrink-0">
              <a
                href={n.path}
                className={`block whitespace-nowrap rounded-md px-3 py-2 text-sm ${
                  n.path === section ? "bg-program text-white" : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
                }`}
              >
                {n.label}
                {n.path === "/countersign" && toSign ? <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-xs font-bold text-white">{toSign}</span> : null}
              </a>
            </li>
          ))}
        </ul>
      </nav>
      <main className="min-w-0 flex-1">
        <ReadinessBanner version={readinessVersion} />
        {path === "/users" && me.capabilities.includes("users.manage") ? (
          <Users me={me} />
        ) : path === "/review" && me.capabilities.includes("record.read") ? (
          <ReviewQueue />
        ) : recordKey && me.capabilities.includes("record.read") ? (
          <RecordScreen key={recordKey} personKey={recordKey} me={me} />
        ) : path === "/pending" && me.capabilities.includes("reports.screen") ? (
          <Pending />
        ) : path.startsWith("/authorizations/") && me.capabilities.includes("record.read") ? (
          <Authorization key={path} path={path} me={me} />
        ) : section === "/visits" && me.capabilities.includes("record.read") ? (
          <Visits path={path} me={me} />
        ) : path.startsWith("/chart/") && me.capabilities.includes("record.read") ? (
          <Chart key={path} personKey={decodeURIComponent(path.slice("/chart/".length))} />
        ) : path.startsWith("/otc/") && me.capabilities.includes("station.use") ? (
          <Otc key={path} personKey={decodeURIComponent(path.slice("/otc/".length))} />
        ) : section === "/station" && me.capabilities.includes("station.use") ? (
          <Station path={path} me={me} />
        ) : section === "/print" && me.capabilities.includes("export.phi") ? (
          <PrintCenter path={path} me={me} />
        ) : path === "/missed" && me.capabilities.includes("station.use") ? (
          <Missed />
        ) : path === "/countersign" && me.capabilities.includes("record.read") ? (
          <Countersign />
        ) : section === "/pass" && me.capabilities.includes("station.use") ? (
          <Pass path={path} me={me} />
        ) : section === "/staff" && me.capabilities.includes("record.read") ? (
          <Staff key={path} path={path} />
        ) : path === "/supplies" && me.capabilities.includes("supplies.manage") ? (
          <Supplies />
        ) : path === "/cities" && me.capabilities.includes("city.assign") ? (
          <Cities />
        ) : section === "/reports" && me.capabilities.includes("reports.screen") ? (
          <Reports path={path} me={me} />
        ) : path === "/config" && me.capabilities.includes("config.operational") ? (
          <Config onReadiness={() => setReadinessVersion((v) => v + 1)} />
        ) : path === "/roster" && me.capabilities.includes("record.read") ? (
          <Roster />
        ) : path === "/account" ? (
          <Account me={me} onSaved={onMe} />
        ) : path === "/test-outbox" && environment === "test" ? (
          <TestOutbox />
        ) : path === "/test-reset" && environment === "test" ? (
          <TestReset />
        ) : here ? (
          <div className="section-card">
            <h2 className="text-lg font-semibold">{here.label}</h2>
            <p className="mt-2 text-sm text-slate-600">Not available yet.</p>
          </div>
        ) : (
          <div className="section-card">
            <h2 className="text-lg font-semibold">Welcome, {me.name}</h2>
            <p className="mt-2 text-sm text-slate-600">
              Signed in as {me.roleLabel}. Choose a section. Sections appear as they are built.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function App(): React.ReactElement {
  const [cfg, setCfg] = useState<PublicConfig | null>(null);
  const [down, setDown] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>();
  const [warnLeft, setWarnLeft] = useState<number | null>(null);
  const [testClock] = useTestClock(cfg?.environment === "test" && Boolean(me));
  const lastActivity = useRef(Date.now());
  const lastKeepalive = useRef(Date.now());
  /** True while the one server check after a local idle expiry is in flight. */
  const expiring = useRef(false);
  /** The current user, readable from callbacks without re-creating them. */
  const meRef = useRef(me);
  meRef.current = me;

  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const params = new URLSearchParams(window.location.search);

  useEffect(() => {
    void api<PublicConfig>("GET", "/api/public-config").then((r) => {
      if (r.ok) {
        setCfg(r.data);
        applyBranding(r.data);
      } else setDown(r.data.error ?? "The service is not responding.");
    });
  }, []);

  const ended = useCallback((reason?: string) => {
    setMe(null);
    setWarnLeft(null);
    setNotice(
      reason === "idle"
        ? `You were signed out after ${cfg?.idleMinutes ?? 30} minutes without activity.`
        : reason === "credentialsChanged" || reason === "deactivated"
          ? "Your access changed, so you were signed out. Sign in again."
          : reason
            ? "Your session has ended. Sign in again."
            : undefined
    );
  }, [cfg]);

  /**
   * Peek at the session (never extends it). On 200, also re-sync the local
   * idle clock to the server's lastSeen: the server is the authority.
   */
  const checkSession = useCallback(async () => {
    const r = await api<{ user: Me; session: { lastSeen: number; serverNow: number; clockNow?: number } }>(
      "GET",
      "/api/auth/session?peek=1"
    );
    if (r.ok) {
      lastActivity.current = syncedLastActivity(Date.now(), r.data.session.serverNow, r.data.session.lastSeen);
      syncClock(r.data.session.serverNow, r.data.session.clockNow);
      // Keep the same object when nothing changed, so a poll does not re-run
      // every effect and re-render every screen keyed on the user.
      setMe((prev) => (prev && JSON.stringify(prev) === JSON.stringify(r.data.user) ? prev : r.data.user));
    } else if (r.status === 401) {
      if (meRef.current) ended(r.data.reason ?? "ended");
      else setMe(null);
    } else if (r.status === 503) setDown(r.data.error ?? "Unavailable.");
  }, [ended]);

  useEffect(() => {
    if (path === "/login/verify" || path === "/login/pin") {
      setMe(null);
      return;
    }
    void checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Background poll (does not count as activity): picks up a server-side end
  // (rotated PIN, deactivation, idle) within a minute.
  useEffect(() => {
    if (!me) return;
    const id = window.setInterval(() => void checkSession(), POLL_MS);
    return () => window.clearInterval(id);
  }, [me, checkSession]);

  // Idle tracking. Real input re-arms the server's idle clock (throttled); the
  // warning appears 5 minutes before the server would end the session.
  useEffect(() => {
    if (!me || !cfg) return;
    const idleMs = cfg.idleMinutes * 60_000;
    let trailing: number | null = null;
    const sendKeepalive = () => {
      trailing = null;
      lastKeepalive.current = Date.now();
      void api("POST", "/api/auth/keepalive").then((r) => r.status === 401 && ended(r.data.reason ?? "ended"));
    };
    const onActivity = () => {
      lastActivity.current = Date.now();
      const a = keepaliveAction(Date.now(), lastKeepalive.current, KEEPALIVE_EVERY_MS, trailing !== null);
      if (a.kind === "sendNow") sendKeepalive();
      else if (a.kind === "schedule") trailing = window.setTimeout(sendKeepalive, a.atMs - Date.now());
    };
    const events = ["pointerdown", "keydown", "touchstart", "scroll"] as const;
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const tick = window.setInterval(() => {
      const last = Math.max(lastActivity.current, lastKeepalive.current, lastServerActivityMs());
      const phase = idlePhase(Date.now(), last, idleMs, WARN_BEFORE_MS);
      if (phase === "expired") {
        // Ask the server ONCE and accept its answer (see idle.ts). Never end
        // locally and then let a later check sign back in.
        if (!expiring.current) {
          expiring.current = true;
          void checkSession().finally(() => {
            expiring.current = false;
          });
        }
      } else if (phase === "warn") setWarnLeft(secondsLeft(Date.now(), last, idleMs));
      else setWarnLeft(null);
    }, 1000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      window.clearInterval(tick);
      if (trailing !== null) window.clearTimeout(trailing);
    };
  }, [me, cfg, ended, checkSession]);

  async function signOut() {
    await api("POST", "/api/auth/logout");
    setMe(null);
    setWarnLeft(null);
    setNotice("You're signed out.");
    window.history.replaceState(null, "", "/");
  }

  async function stay() {
    lastActivity.current = Date.now();
    lastKeepalive.current = Date.now();
    const r = await api("POST", "/api/auth/keepalive");
    if (r.status === 401) ended(r.data.reason ?? "ended");
    else setWarnLeft(null);
  }

  let body: React.ReactNode;
  if (down) body = <Unavailable message={down} />;
  else if (path === "/login/verify" || path === "/login/pin")
    body = (
      <div className="mx-auto max-w-md">
        <PinEntry
          token={params.get("t") ?? ""}
          support={cfg}
          onSignedIn={() => {
            setNotice(undefined);
            void checkSession();
          }}
        />
      </div>
    );
  else if (me === undefined) body = <p className="text-sm text-slate-600">Loading…</p>;
  else if (me === null)
    body = (
      <div className="mx-auto max-w-md">
        <SignIn notice={notice} />
      </div>
    );
  else body = <Home me={me} path={path} environment={cfg?.environment} onMe={setMe} />;

  // After a PIN sign-in the URL is "/", but the PinEntry branch keeps
  // rendering until the session check lands; route on the session then.
  const signedInOnLoginPath = me && (path === "/login/verify" || path === "/login/pin");

  return (
    <div className="min-h-screen">
      <Header cfg={cfg} me={me ?? null} onSignOut={signOut} testClock={testClock} />
      <div className="mx-auto max-w-6xl px-4 py-6">
        {signedInOnLoginPath ? <Home me={me} path="/" environment={cfg?.environment} onMe={setMe} /> : body}
      </div>
      <SupportFooter c={cfg} />
      {me && warnLeft !== null && <IdleWarning secondsLeft={warnLeft} onStay={stay} onSignOut={signOut} />}
    </div>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
